import type {
  ProgramGoalProfile,
  ProgramPhaseArtifact,
  ProgramTrainingStrategy,
  ProgramExerciseAllocation,
  ProgramWeeklyScheduleSplit,
  ProgramWeeklySplitDesign,
  ProgramWorkoutBlueprint,
} from './fitness-plan.domain';
import type { FitnessPlanWorkout } from './fitness-plan.types';

function hasText(value: string | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function goalProfileHasDisplay(
  profile: ProgramGoalProfile | undefined,
): boolean {
  if (!profile) {
    return false;
  }
  return (
    hasText(profile.interpretation) ||
    hasText(profile.primaryDisplay) ||
    hasText(profile.primary) ||
    (profile.distributedFocuses?.length ?? 0) > 0 ||
    (profile.programWideRules?.length ?? 0) > 0 ||
    (profile.equipmentConstraints?.length ?? 0) > 0 ||
    (profile.sessionConstraints?.length ?? 0) > 0
  );
}

export function trainingStrategyHasDisplay(
  strategy: ProgramTrainingStrategy | undefined,
): boolean {
  if (!strategy) {
    return false;
  }
  return (
    (strategy.prioritizedMuscles?.length ?? 0) > 0 ||
    (strategy.maintainedMuscles?.length ?? 0) > 0 ||
    (strategy.deemphasizedMuscles?.length ?? 0) > 0 ||
    typeof strategy.weeklyFrequency === 'number' ||
    (strategy.weeklyVolumeTargets?.length ?? 0) > 0 ||
    hasText(strategy.trainingStyle) ||
    hasText(strategy.equipmentConstraints) ||
    hasText(strategy.rationale)
  );
}

export function programPhaseHasDisplay(
  phase: ProgramPhaseArtifact | undefined,
): boolean {
  if (!phase) {
    return false;
  }
  return (
    hasText(phase.name) ||
    typeof phase.durationWeeks === 'number' ||
    hasText(phase.focus) ||
    hasText(phase.progressionStyle) ||
    hasText(phase.aggressiveness) ||
    (phase.successMetrics?.length ?? 0) > 0 ||
    hasText(phase.rationale)
  );
}

export function weeklySplitHasDisplay(
  weeklySplit: ProgramWeeklySplitDesign | undefined,
): boolean {
  if (!weeklySplit) {
    return false;
  }
  return (
    hasText(weeklySplit.splitPattern) ||
    typeof weeklySplit.trainingDays === 'number' ||
    (weeklySplit.sessionTemplates?.length ?? 0) > 0 ||
    hasText(weeklySplit.rationale)
  );
}

export function weeklyScheduleSplitHasDisplay(
  schedule: ProgramWeeklyScheduleSplit | ProgramWeeklySplitDesign | undefined,
): boolean {
  if (!schedule) {
    return false;
  }
  const modern = schedule as ProgramWeeklyScheduleSplit;
  if ((modern.scheduledSessions?.length ?? 0) > 0) {
    return true;
  }
  return weeklySplitHasDisplay(schedule as ProgramWeeklySplitDesign);
}

export function workoutBlueprintVolumeRows(
  blueprint: ProgramWorkoutBlueprint,
): NonNullable<ProgramWorkoutBlueprint['targetMuscleVolumes']> {
  return (blueprint.targetMuscleVolumes ?? []).filter(
    (row) => hasText(row.muscle) || (row.targetSets ?? 0) > 0,
  );
}

export function workoutBlueprintHasDisplay(
  blueprint: ProgramWorkoutBlueprint | undefined | null,
): boolean {
  if (!blueprint) {
    return false;
  }
  return (
    hasText(blueprint.name) ||
    hasText(blueprint.focus) ||
    hasText(blueprint.purpose) ||
    (blueprint.targetMuscles?.length ?? 0) > 0 ||
    (blueprint.emphasizedAreas?.length ?? 0) > 0 ||
    workoutBlueprintVolumeRows(blueprint).length > 0 ||
    (blueprint.addressesSelectionIds ?? []).some((id) => hasText(id)) ||
    hasText(blueprint.recoveryNotes) ||
    hasText(blueprint.rationale)
  );
}

export function renderableWorkoutBlueprints(
  blueprints: ProgramWorkoutBlueprint[] | undefined,
): ProgramWorkoutBlueprint[] {
  return (blueprints ?? []).filter(workoutBlueprintHasDisplay);
}

/** Stable @for track keys — do not use ``?? $index`` in track expressions (breaks Ivy). */
export function workoutBlueprintTrackKey(
  blueprint: ProgramWorkoutBlueprint,
  index: number,
): string {
  const id =
    typeof blueprint.blueprintId === 'string'
      ? blueprint.blueprintId.trim()
      : '';
  if (id) {
    return id;
  }
  const name = typeof blueprint.name === 'string' ? blueprint.name.trim() : '';
  if (name) {
    return `name:${name}:${index}`;
  }
  const focus =
    typeof blueprint.focus === 'string' ? blueprint.focus.trim() : '';
  if (focus) {
    return `focus:${focus}:${index}`;
  }
  return `idx:${index}`;
}

export function renderableWorkoutBlueprintsHaveDisplay(
  blueprints: ProgramWorkoutBlueprint[] | undefined,
): boolean {
  return renderableWorkoutBlueprints(blueprints).length > 0;
}

export function workoutBlueprintsHaveDisplay(
  blueprints: ProgramWorkoutBlueprint[] | undefined,
): boolean {
  return renderableWorkoutBlueprints(blueprints).length > 0;
}

export function exerciseAllocationHasDisplay(
  allocation: ProgramExerciseAllocation | undefined | null,
): boolean {
  if (!allocation) {
    return false;
  }
  const rows = allocation.muscleAllocations ?? [];
  return rows.some(
    (row) =>
      hasText(row.muscle) &&
      ((row.movementPatterns?.length ?? 0) > 0 ||
        (row.exercises ?? []).some(
          (ex) => hasText(ex.exerciseName) && (ex.sets ?? 0) > 0,
        )),
  );
}

export function renderableExerciseAllocations(
  allocations: ProgramExerciseAllocation[] | undefined,
): ProgramExerciseAllocation[] {
  return (allocations ?? []).filter(exerciseAllocationHasDisplay);
}

export function exerciseAllocationsHaveDisplay(
  allocations: ProgramExerciseAllocation[] | undefined,
): boolean {
  return renderableExerciseAllocations(allocations).length > 0;
}

export function exerciseAllocationTrackKey(
  allocation: ProgramExerciseAllocation,
  index: number,
): string {
  const bid = allocation.blueprintId?.trim();
  if (bid) {
    return `bp:${bid}`;
  }
  const name = allocation.workoutName?.trim();
  if (name) {
    return `name:${name}:${index}`;
  }
  return `idx:${index}`;
}

export function generatedWorkoutHasDisplay(
  workout: FitnessPlanWorkout,
): boolean {
  return hasText(workout.name) && (workout.exercises?.length ?? 0) > 0;
}

export function generatedWorkoutsHaveDisplay(
  workouts: FitnessPlanWorkout[] | undefined,
): boolean {
  return (workouts ?? []).some(generatedWorkoutHasDisplay);
}

export function exerciseDisplayName(exercise: {
  exerciseName?: string;
  name?: string;
}): string {
  const primary =
    typeof exercise.exerciseName === 'string'
      ? exercise.exerciseName.trim()
      : '';
  if (primary) {
    return primary;
  }
  const legacy = typeof exercise.name === 'string' ? exercise.name.trim() : '';
  return legacy || 'Exercise';
}
